import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { of, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import { SubmittedAssignment } from '../../../../models/submit.assignment.school.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolAssignmentService } from '../../../../services/assignment.school.service';
import { UtilsService } from '../../../../services/utils.service';
import { AppState } from '../../../../state/appstate';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { S3DocumentUploadComponent } from '../../../components/uploaders/s3-document-upload/s3-document-upload.component';

@Component({
  selector: 'app-dlg-submit-assignment',
  standalone: true,
  imports: [S3DocumentUploadComponent, AsyncPipe],
  templateUrl: './dlg-submit-assignment.component.html',
  styleUrl: './dlg-submit-assignment.component.css',
})
export class DlgSubmitAssignmentComponent implements OnInit, OnDestroy {
  @ViewChild('uploader') uploader!: S3DocumentUploadComponent;
  store: Store<AppState> = inject(Store);
  toastr: ToastrService = inject(ToastrService);
  assignmentService = inject(SchoolAssignmentService);
  readonly dialogRef = inject(MatDialogRef<DlgSubmitAssignmentComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  private dialog: MatDialog = inject(MatDialog);

  title: string = 'Upload Your Assignment';
  description: string = 'Upload your completed assignment for grading';
  action: string = 'Submit';

  uuid = uuidv4();

  loading$: any = of(false);

  user: UserModel | null = null;

  exit$: Subject<void> = new Subject<void>();

  preSignedUrlEndpoint: string =
    environment.AWS_SCHOOL_ASSIGNMENTS_STUDENTWORK_SIGNEDURL;
  apiKey: string = environment.AWS_API_KEY;
  metadata: any = {};
  cloudFrontDomain = environment.AWS_USERS_CLOUDFRONT;
  maxFileSize = 10485760; // 10 MB

  constructor() {}

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = 'auto';
    this.exit$.next();
    this.exit$.complete();
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async submit(): Promise<void> {
    this.loading$ = of(true);

    this.uploader?.setInputParams({
      groupId: this.data.groupId,
      academicYear: this.data.academicYear,
      gradeId: this.data.gradeId,
      studentWorkId: this.uuid,
    });

    this.uploader?.setMetadata({
      groupId: this.data.groupId,
      academicYear: this.data.academicYear,
      gradeId: this.data.gradeId,
      studentWorkId: this.uuid,
    });

    this.uploader
      .upload()
      .then((result) => {
        // console.log(`Upload result: ${JSON.stringify(result, null, 2)}`);
        if (result?.successful && result.successful[0]?.response?.uploadURL) {
          this.createDatabaseEntry(
            this.uuid,
            result.successful[0].name,
            result.successful[0].response?.uploadURL,
          );
        } else {
          this.loading$ = of(false);
          this.onShowAlert({
            code: 'ERR_SUBMIT_ASSIGNMENT',
            message: 'Failed to upload document. Please try again later',
            title: 'Error Uploading Document',
          });
        }
      })
      .catch((error) => {
        this.loading$ = of(false);
        this.onShowAlert({
          code: 'ERR_SUBMIT_ASSIGNMENT',
          message: 'Failed to upload document. Please try again later',
          title: 'Error Uploading Document',
        });
      });
  }

  async createDatabaseEntry(
    id: string,
    name?: string,
    url?: string | undefined,
  ) {
    let data: SubmittedAssignment = {
      id: this.uuid,
      groupId: this.data.groupId,
      academicYear: this.data.academicYear,
      gradeId: this.data.gradeId,
      assignmentId: this.data.assignmentId,
      studentName: this.data.studentName,
      studentId: this.data.studentId,
      studentWorkId: this.uuid,
      createdBy: this.user?.name,
      createdById: this.user?.uid,
      fileName: name,
      created: Timestamp.now(),
      modified: Timestamp.now(),
    };

    // console.log(
    //   `DlgSubmitAssignment :: createDatabaseEntry: ${JSON.stringify(data)}`
    // );

    if (url)
      data['url'] = UtilsService.replaceDomain(url, this.cloudFrontDomain);

    try {
      const result = await this.assignmentService.submitStudentWork(data);
      // console.log(`Student work submitted: ${result}`);
      this.loading$ = of(false);
      this.dialogRef.close(true);
    } catch (error) {
      console.log(`Error submitting student work: ${error}`);
      this.loading$ = of(false);
      this.onShowAlert(error as AppExceptionModel);
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title,
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
